import { Directive, ElementRef, inject, input, model } from "@angular/core";
import {
    getFirstNavigableElementBetween,
    GetFirstNavigableElementBetweenOptions
} from "../../helpers";
import { InteractivityChecker } from "@angular/cdk/a11y";

@Directive({
    selector: "[lgArrowNavigation]",
    standalone: true,
    host: {
        "(keydown)": "_onKeyDown($event)"
    }
})
export class LgArrowNavigationDirective {
    private _root = inject(ElementRef).nativeElement;
    private _interactivityChecker = inject(InteractivityChecker);

    // note: we really want `input()` signals here, but we also want to be able to bind the directive on host,
    // and currently setting that kind of input doesn't seem to be possible (sigh)

    /** Indicate if the navigation is horizontal (arrows left/right) or vertical */
    horizontalNavigation = model(true);
    /** Indicates if the navigation is disabled */
    disabledNavigation = model(false);
    /** Indicates if the keys should be passed through when the navigation is at its end (i.e. when the key press had no effect) */
    navigationPassThrough = input(false);
    /** Optional selector to limit the navigation to elements matching this selector */
    navigationSelector = input<string | null>(null);
    /** Indicates if only tabbable (non-negative tabindex) elements should be navigated to */
    navigationTabbableOnly = input(false);
    /** Specify if only arrows should be allowed (no pageup/pagedown/home/end keys) */
    navigationArrowsOnly = input(false);

    protected _onKeyDown(event: KeyboardEvent): void {
        if (this.disabledNavigation()) return;
        const active = document.activeElement as HTMLElement;
        const isHorizontal = this.horizontalNavigation();
        const arrowsOnly = this.navigationArrowsOnly();
        let newTarget: HTMLElement | null = null;
        let consume = false;
        if (
            (isHorizontal && event.key === "ArrowLeft") ||
            (!isHorizontal && event.key === "ArrowUp")
        ) {
            // Move focus to the previous element
            consume = true;
            newTarget = getFirstNavigableElementBetween(
                this._root,
                null,
                active,
                this._interactivityChecker,
                this._getOptions()
            );
        } else if (
            (isHorizontal && event.key === "ArrowRight") ||
            (!isHorizontal && event.key === "ArrowDown")
        ) {
            consume = true;
            newTarget = getFirstNavigableElementBetween(
                this._root,
                active,
                null,
                this._interactivityChecker,
                this._getOptions()
            );
        } else if (!arrowsOnly && (event.key === "PageUp" || event.key === "Home")) {
            consume = true;
            newTarget = getFirstNavigableElementBetween(
                this._root,
                null,
                null,
                this._interactivityChecker,
                this._getOptions()
            );
        } else if (!arrowsOnly && (event.key === "PageDown" || event.key === "End")) {
            consume = true;
            newTarget = getFirstNavigableElementBetween(
                this._root,
                null,
                true,
                this._interactivityChecker,
                this._getOptions()
            );
        }
        if (consume) {
            if (!this.navigationPassThrough() || (newTarget && newTarget !== active)) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
            }
        }
        if (newTarget && newTarget !== active) {
            newTarget.focus();
        }
    }

    private _getOptions(): GetFirstNavigableElementBetweenOptions {
        return {
            tabbableOnly: this.navigationTabbableOnly(),
            selector: this.navigationSelector() || undefined
        };
    }
}
